/**
 * Hybrid Logical Clock (HLC) Implementation
 *
 * A Hybrid Logical Clock combines physical time (wall clock) with logical counters
 * to provide a causality-preserving timestamp for distributed systems.
 *
 * HLC Properties:
 * - Captures happens-before relationships (causality)
 * - Close to physical time (human-readable, reflects actual event order)
 * - Monotonically increasing within each node
 * - Provides deterministic ordering across all nodes
 *
 * Use Cases:
 * - CRDT Last-Write-Wins (LWW) conflict resolution
 * - Distributed event ordering
 * - Causal consistency in offline-first apps
 *
 * @see https://cse.buffalo.edu/tech-reports/2014-04.pdf (HLC Paper)
 */

/**
 * Hybrid Logical Clock timestamp structure
 *
 * @interface HybridLogicalClock
 * @property {number} physical - Physical time component (milliseconds since epoch)
 * @property {number} logical - Logical counter (increments when physical time doesn't advance)
 * @property {string} client_uuid - Unique client identifier for deterministic tie-breaking
 *
 * @example
 * const timestamp: HybridLogicalClock = {
 *   physical: 1738123456789,
 *   logical: 3,
 *   client_uuid: 'abc-123-def'
 * };
 */
export interface HybridLogicalClock {
  physical: number;
  logical: number;
  client_uuid: string;
}

/**
 * Hybrid Logical Clock Service
 *
 * Manages HLC state for a single client/node. Provides methods to:
 * - Generate timestamps for local events
 * - Update clock when receiving remote timestamps (causality tracking)
 * - Compare timestamps for ordering
 * - Serialize/deserialize timestamps
 *
 * @class HybridLogicalClockService
 *
 * @example
 * const clientUUID = await getClientUUID();
 * const hlc = new HybridLogicalClockService(clientUUID);
 *
 * // Generate timestamp for local event
 * const timestamp1 = hlc.now();
 *
 * // Receive remote event and update clock
 * const remoteTimestamp = { physical: 1738123456789, logical: 2, client_uuid: 'remote-client' };
 * const timestamp2 = hlc.receive(remoteTimestamp);
 *
 * // Compare timestamps
 * const cmp = HybridLogicalClockService.compare(timestamp1, timestamp2);
 * if (cmp < 0) {
 *   console.log('timestamp1 happened before timestamp2');
 * }
 */
export class HybridLogicalClockService {
  private physical: number = 0;
  private logical: number = 0;
  private clientUUID: string;

  /**
   * Creates a new HybridLogicalClockService instance
   *
   * @param {string} clientUUID - Unique identifier for this client/node
   *                              Used for deterministic tie-breaking when timestamps are identical
   */
  constructor(clientUUID: string) {
    this.clientUUID = clientUUID;
  }

  /**
   * Generate a new timestamp for a local event
   *
   * Algorithm:
   * - If physical time has advanced, use current physical time and reset logical counter to 0
   * - If physical time hasn't advanced (same millisecond), increment logical counter
   *
   * This ensures monotonicity: each call to now() produces a timestamp strictly greater
   * than the previous call, even if multiple events occur in the same millisecond.
   *
   * @returns {HybridLogicalClock} New timestamp with updated physical/logical components
   *
   * @example
   * const hlc = new HybridLogicalClockService('client-1');
   * const ts1 = hlc.now(); // { physical: 1738123456789, logical: 0, client_uuid: 'client-1' }
   * const ts2 = hlc.now(); // { physical: 1738123456789, logical: 1, client_uuid: 'client-1' } (same ms)
   */
  now(): HybridLogicalClock {
    const physicalNow = Date.now();

    if (physicalNow > this.physical) {
      this.physical = physicalNow;
      this.logical = 0;
    } else {
      this.logical++;
      // Guard against unbounded growth in extreme high-frequency scenarios.
      // In practice this is unreachable (would require ~9 quadrillion events in one ms).
      if (this.logical >= Number.MAX_SAFE_INTEGER) {
        console.warn('[HLC] Logical counter approaching MAX_SAFE_INTEGER, forcing physical time advance');
        this.physical = Date.now();
        this.logical = 0;
      }
    }

    return {
      physical: this.physical,
      logical: this.logical,
      client_uuid: this.clientUUID
    };
  }

  /**
   * Update clock when receiving a remote timestamp (maintains causality)
   *
   * This method ensures the local clock advances to reflect the causal relationship
   * with the remote event. The local clock will always be >= the remote clock after
   * calling receive(), which preserves the happens-before relationship.
   *
   * Algorithm:
   * - Calculate max physical time across: current time, local clock, and remote clock
   * - If max is strictly greater than both local and remote:
   *   - Use max as physical time, reset logical counter to 0
   * - If max equals both local and remote physical time:
   *   - Use max(local.logical, remote.logical) + 1
   * - If max equals only local physical time:
   *   - Increment local logical counter
   * - Otherwise:
   *   - Use max physical time, set logical = remote.logical + 1
   *
   * @param {HybridLogicalClock} remote - Timestamp from remote client/node
   * @returns {HybridLogicalClock} Updated local timestamp reflecting causality
   *
   * @example
   * const hlc = new HybridLogicalClockService('client-1');
   * const ts1 = hlc.now(); // { physical: 1000, logical: 0, client_uuid: 'client-1' }
   *
   * // Receive event from future
   * const remoteFuture = { physical: 2000, logical: 0, client_uuid: 'client-2' };
   * const ts2 = hlc.receive(remoteFuture); // { physical: 2000, logical: 1, client_uuid: 'client-1' }
   *
   * // Receive event from past (clock doesn't go backward)
   * const remotePast = { physical: 500, logical: 0, client_uuid: 'client-2' };
   * const ts3 = hlc.receive(remotePast); // { physical: 2000, logical: 2, client_uuid: 'client-1' }
   */
  receive(remote: HybridLogicalClock): HybridLogicalClock {
    const physicalNow = Date.now();
    const maxPhysical = Math.max(physicalNow, this.physical, remote.physical);

    if (maxPhysical > this.physical && maxPhysical > remote.physical) {
      this.physical = maxPhysical;
      this.logical = 0;
    } else if (maxPhysical === this.physical && maxPhysical === remote.physical) {
      this.logical = Math.max(this.logical, remote.logical) + 1;
    } else if (maxPhysical === this.physical) {
      this.logical++;
    } else {
      this.physical = maxPhysical;
      this.logical = remote.logical + 1;
    }

    // Guard against unbounded growth (matching now() behavior)
    if (this.logical >= Number.MAX_SAFE_INTEGER) {
      console.warn('[HLC] Logical counter approaching MAX_SAFE_INTEGER in receive(), forcing physical time advance');
      this.physical = Date.now();
      this.logical = 0;
    }

    return {
      physical: this.physical,
      logical: this.logical,
      client_uuid: this.clientUUID
    };
  }

  /**
   * Returns true if `a` wins over `b` in Last-Write-Wins conflict resolution.
   * Later timestamp wins; on exact tie, lower UUID wins (deterministic).
   */
  static winsOver(a: HybridLogicalClock, b: HybridLogicalClock): boolean {
    return HybridLogicalClockService.compare(a, b) > 0;
  }

  /**
   * Compare two HLC timestamps for LWW ordering
   *
   * Comparison Order:
   * 1. Physical time (primary): Earlier physical time means earlier timestamp
   * 2. Logical counter (secondary): Lower logical counter means earlier timestamp
   * 3. Client UUID (tie-breaker): Used for deterministic ordering when physical+logical are identical
   *
   * Special LWW Semantics for Client UUID Tie-Breaking:
   * - When physical and logical are equal, the comparison is INVERTED for client_uuid
   * - This means: lower UUID "wins" in Last-Write-Wins conflict resolution
   * - Example: If a.client_uuid < b.client_uuid, returns 1 (a is "greater" / wins)
   *
   * @param {HybridLogicalClock} a - First timestamp
   * @param {HybridLogicalClock} b - Second timestamp
   * @returns {number} Comparison result:
   *                   -1 if a happened before b (a < b)
   *                    0 if timestamps are identical
   *                    1 if a happened after b (a > b)
   *
   * @example
   * const ts1 = { physical: 1000, logical: 0, client_uuid: 'client-1' };
   * const ts2 = { physical: 2000, logical: 0, client_uuid: 'client-2' };
   * const ts3 = { physical: 1000, logical: 1, client_uuid: 'client-1' };
   * const ts4 = { physical: 1000, logical: 0, client_uuid: 'client-1' };
   *
   * HybridLogicalClockService.compare(ts1, ts2); // -1 (ts1 is earlier)
   * HybridLogicalClockService.compare(ts1, ts3); // -1 (same physical, lower logical)
   * HybridLogicalClockService.compare(ts1, ts4); // 0 (identical)
   * HybridLogicalClockService.compare(ts2, ts1); // 1 (ts2 is later)
   */
  static compare(a: HybridLogicalClock, b: HybridLogicalClock): number {
    if (a.physical !== b.physical) {
      return a.physical < b.physical ? -1 : 1;
    }
    if (a.logical !== b.logical) {
      return a.logical < b.logical ? -1 : 1;
    }
    // Tie-break by client UUID (deterministic ordering)
    // For LWW semantics: lower UUID wins, so invert comparison
    // (a < b returns 1, meaning a "wins" / is "greater" in LWW sense)
    if (a.client_uuid !== b.client_uuid) {
      return a.client_uuid < b.client_uuid ? 1 : -1;
    }
    return 0;
  }

  /**
   * Serialize HLC timestamp to string format
   *
   * Format: "physical-logical-client_uuid"
   * Example: "1738123456789-3-abc-123-def"
   *
   * This compact format is used for:
   * - Storing timestamps in CRDT metadata
   * - Transmitting timestamps over network
   * - Comparison and debugging
   *
   * @param {HybridLogicalClock} hlc - Timestamp to serialize
   * @returns {string} String representation in "physical-logical-client_uuid" format
   *
   * @example
   * const ts = { physical: 1738123456789, logical: 3, client_uuid: 'abc-123' };
   * const str = HybridLogicalClockService.toString(ts);
   * console.log(str); // "1738123456789-3-abc-123"
   */
  static toString(hlc: HybridLogicalClock): string {
    return `${hlc.physical}-${hlc.logical}-${hlc.client_uuid}`;
  }

  /**
   * Deserialize HLC timestamp from string format
   *
   * Parses string format "physical-logical-client_uuid" back into HybridLogicalClock object.
   * Handles client UUIDs that contain dashes by only splitting on the first two dashes.
   *
   * @param {string} str - Serialized timestamp in "physical-logical-client_uuid" format
   * @returns {HybridLogicalClock} Parsed timestamp object
   * @throws {Error} If timestamp format is invalid or contains invalid values
   *
   * @example
   * const str = "1738123456789-3-abc-123-def";
   * const ts = HybridLogicalClockService.fromString(str);
   * console.log(ts);
   * // {
   * //   physical: 1738123456789,
   * //   logical: 3,
   * //   client_uuid: 'abc-123-def'
   * // }
   *
   * @example
   * // Invalid format throws error
   * try {
   *   HybridLogicalClockService.fromString("invalid");
   * } catch (e) {
   *   console.error(e.message); // "Invalid HLC timestamp format"
   * }
   */
  static fromString(str: string): HybridLogicalClock {
    // Validate input
    if (!str || typeof str !== 'string') {
      throw new Error('Invalid HLC timestamp: must be a non-empty string');
    }

    // Parse format: "physical-logical-client_uuid"
    // Split only on first two dashes (client_uuid may contain dashes)
    const parts = str.split('-');

    if (parts.length < 3) {
      throw new Error(`Invalid HLC timestamp format: "${str}" (expected format: "physical-logical-client_uuid")`);
    }

    const physical = parseInt(parts[0], 10);
    const logical = parseInt(parts[1], 10);
    const client_uuid = parts.slice(2).join('-'); // Rejoin remaining parts with dashes

    // Validate parsed values
    if (isNaN(physical) || physical < 0) {
      throw new Error(`Invalid HLC physical time: "${parts[0]}" (must be non-negative integer)`);
    }

    if (isNaN(logical) || logical < 0) {
      throw new Error(`Invalid HLC logical counter: "${parts[1]}" (must be non-negative integer)`);
    }

    if (!client_uuid || client_uuid.trim() === '') {
      throw new Error('Invalid HLC timestamp: missing client_uuid');
    }

    return {
      physical,
      logical,
      client_uuid
    };
  }
}
